import type { ScheduleStore } from "@/Modules/Schedule/Store";
import type { AppointmentTransformerType } from "@/Modules/Transformers/Appointment";
import type { Appointment } from "@/Modules/Models/Domain/Schedule";
import type { AppointmentApiQuery } from "@/Services/TherapyApi/Schedule";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";

import { useAppStore, type AppStore } from "@/Modules/App/Store";

import AppointmentTransformer from "@/Modules/Transformers/Appointment";

import { createAppointment } from "@/Services/TherapyApi/Schedule";

import { hasValue } from "@/Utils/Objects";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";

class CreateAppointmentActionHandler {
  constructor(
    private readonly scheduleStore: ScheduleStore,
    private readonly appStore: AppStore = useAppStore(),
    private readonly appointmentTransformer: AppointmentTransformerType = AppointmentTransformer
  ) {}

  public async handle(appointment: AppointmentApiQuery): Promise<Appointment | undefined> {
    const appointmentResp = await createAppointment(appointment).catch((e) => {
      const code = isApiError(e) ? e["error_code"] : null;

      if (code === ApiErrorCodes.SCHEDULE_ALREADY_EXISTS) {
        this.appStore.showSnackbar({
          type: NotificationType.ERROR,
          message: "schedule.therapist.new_appointment.notification.duplicated",
        });
      } else {
        this.appStore.showSnackbar({
          type: NotificationType.ERROR,
          message: "schedule.therapist.new_appointment.notification.error",
        });
      }

      return undefined;
    });

    if (!hasValue(appointmentResp)) return undefined;

    const appointmentDomain = this.appointmentTransformer.toDomain(appointmentResp);

    if (!hasValue(this.scheduleStore.appointments)) {
      this.scheduleStore.appointments = [appointmentDomain];
    } else {
      this.scheduleStore.appointments.push(appointmentDomain);
    }

    this.scheduleStore.dayClicked = this.scheduleStore.halfHourClicked = null;

    this.appStore.showSnackbar({
      type: NotificationType.SUCCESS,
      message: "schedule.therapist.new_appointment.notification.success",
    });

    return appointmentDomain;
  }
}

export default CreateAppointmentActionHandler;
